import { Link } from 'react-router';
import { RelativeTime } from '@/components/RelativeTime';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useConfirm } from '@/hooks/useConfirm';
import {
  type ConfigSegment,
  type ConfigSource,
  type ConfigSourceKind,
  useGatewayConfigSources,
  useRunDhcpProbe,
  useSetDhcpProbe,
  useSetExpectedConfigSources,
} from '@/hooks/useGatewayConfigSources';
import { usePermission } from '@/hooks/usePermission';
import { sourceOffers } from './configSourceDetails';

const TH = 'text-left py-1.5 px-3 text-xs font-medium text-muted-foreground uppercase tracking-wider';

const KINDS: { kind: ConfigSourceKind; title: string; noun: string }[] = [
  { kind: 'ra', title: 'IPv6 routers', noun: 'IPv6 routers' },
  { kind: 'dhcp', title: 'DHCP servers', noun: 'DHCP servers' },
  { kind: 'dhcpv6', title: 'DHCPv6 servers', noun: 'DHCPv6 servers' },
];

function SourceTable({
  sources,
  canUpdate,
  onExpect,
}: {
  sources: ConfigSource[];
  canUpdate: boolean;
  onExpect: (source: ConfigSource, expected: boolean) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-sm">
        <thead className="border-b border-border-subtle">
          <tr>
            <th className={TH}>MAC</th>
            <th className={TH}>Address</th>
            <th className={TH}>Offers</th>
            <th className={TH}>Where</th>
            <th className={TH}>First heard</th>
            <th className={TH}>Last heard</th>
            <th className={TH}>Expected</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border-subtle">
          {sources.map((s) => (
            <tr key={`${s.identity} ${s.via}`} className={s.goneAt ? 'text-muted-foreground' : undefined}>
              <td className="py-1.5 px-3">
                <span className="inline-flex items-center gap-1.5 flex-wrap">
                  <span className="font-mono">{s.mac}</span>
                  {s.expected ? (
                    <Badge variant="secondary" className="font-normal" title="Marked as expected on this segment">
                      Expected
                    </Badge>
                  ) : null}
                  {!s.competes ? (
                    <span
                      className="text-[11px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                      title="Heard, but not offering itself to hosts here, so not counted"
                    >
                      Not counted
                    </span>
                  ) : null}
                </span>
              </td>
              <td className="py-1.5 px-3 font-mono text-xs break-all">{s.address || '—'}</td>
              <td className="py-1.5 px-3 text-xs">{sourceOffers(s)}</td>
              <td className="py-1.5 px-3 text-xs">
                {s.location ? (
                  <Link to={`/devices/${s.location.deviceId}`} className="text-primary hover:underline">
                    {s.location.deviceName} <span className="font-mono">{s.location.interface}</span>
                  </Link>
                ) : (
                  '—'
                )}
              </td>
              <td className="py-1.5 px-3 text-xs text-muted-foreground whitespace-nowrap">
                <RelativeTime value={s.firstSeen} />
              </td>
              <td className="py-1.5 px-3 text-xs text-muted-foreground whitespace-nowrap">
                {s.goneAt ? 'No longer heard' : <RelativeTime value={s.lastHeard} />}
              </td>
              <td className="py-1.5 px-3">
                <Switch
                  checked={s.expected}
                  // A source no longer heard can leave the set but not join it.
                  disabled={!canUpdate || (s.goneAt != null && !s.expected)}
                  aria-label={`Expected: ${s.mac}`}
                  onCheckedChange={(expected) => {
                    onExpect(s, expected);
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Segment({ gatewayId, segment, canUpdate }: { gatewayId: string; segment: ConfigSegment; canUpdate: boolean }) {
  const { confirm, confirmDialog } = useConfirm();
  const setExpected = useSetExpectedConfigSources(gatewayId);
  const setProbe = useSetDhcpProbe(gatewayId);
  const runProbe = useRunDhcpProbe(gatewayId);
  const ref = { interface: segment.interface, vlan: segment.vlan };
  const disabledReason = canUpdate ? undefined : 'You need permission to update this gateway';

  const markExpected = async (kind: ConfigSourceKind, noun: string, count: number) => {
    const ok = await confirm({
      title: `Mark these ${noun} as expected?`,
      message: `The ${String(count)} heard on ${segment.name} now become its expected set. Anything else heard here will raise an incident, even on its own.`,
      confirmText: 'Mark as expected',
    });
    if (ok) setExpected.mutate({ ...ref, kind, expected: true });
  };
  const clearExpected = async (kind: ConfigSourceKind, noun: string) => {
    const ok = await confirm({
      title: `Clear the expected ${noun}?`,
      message: `${segment.name} goes back to the default rule: more than one raises an incident, and one DHCP server or router is presumed valid.`,
      confirmText: 'Clear',
      isDanger: true,
    });
    if (ok) setExpected.mutate({ ...ref, kind, expected: false });
  };

  const outcome = segment.probe?.lastOutcome ?? null;
  return (
    <div className="space-y-3">
      {confirmDialog}
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="text-sm font-medium font-mono">{segment.name}</h3>
        {!segment.listening ? (
          <Badge variant="secondary" className="font-normal" title="The gateway no longer reports listening here">
            Not listening
          </Badge>
        ) : null}
        {segment.incidents.map((i) => (
          <Link key={i.id} to={`/incidents/${i.id}`} className="text-xs text-destructive hover:underline">
            {i.title}
          </Link>
        ))}
      </div>

      {segment.sources.length === 0 ? (
        <p className="text-sm text-muted-foreground">No router advertisement or DHCP server heard here.</p>
      ) : null}

      {KINDS.map(({ kind, title, noun }) => {
        const sources = segment.sources.filter((s) => s.kind === kind);
        if (sources.length === 0) return null;
        const expected = segment.expectedKinds.includes(kind);
        const heard = sources.filter((s) => !s.goneAt).length;
        return (
          <div key={kind}>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</h4>
              {expected ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!canUpdate || setExpected.isPending}
                  title={disabledReason}
                  onClick={() => void clearExpected(kind, noun)}
                >
                  Clear expected
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!canUpdate || heard === 0 || setExpected.isPending}
                  title={disabledReason ?? 'Make the ones heard now this segment’s expected set'}
                  onClick={() => void markExpected(kind, noun, heard)}
                >
                  Mark as expected
                </Button>
              )}
            </div>
            <SourceTable
              sources={sources}
              canUpdate={canUpdate && !setExpected.isPending}
              onExpect={(source, isExpected) => {
                setExpected.mutate({ ...ref, kind, identity: source.identity, via: source.via, expected: isExpected });
              }}
            />
          </div>
        );
      })}

      <div className="flex items-center gap-3 flex-wrap text-sm">
        <label className="inline-flex items-center gap-2">
          <Switch
            checked={segment.probe != null}
            disabled={!canUpdate || setProbe.isPending}
            onCheckedChange={(enabled) => {
              setProbe.mutate({ ...ref, enabled });
            }}
          />
          <span title="Every 30 minutes the gateway sends one DHCPDISCOVER and one DHCPv6 Solicit here and counts the offers. It never takes a lease.">
            DHCP probe
          </span>
        </label>
        <Button
          variant="outline"
          size="sm"
          disabled={!canUpdate || runProbe.isPending}
          title={disabledReason}
          onClick={() => {
            runProbe.mutate(ref);
          }}
        >
          {runProbe.isPending ? 'Probing…' : 'Probe now'}
        </Button>
        {segment.probe?.lastProbedAt ? (
          <span className="text-xs text-muted-foreground">
            Last probe <RelativeTime value={segment.probe.lastProbedAt} />
            {outcome
              ? outcome.sent
                ? `: ${String(outcome.servers.length)} server${outcome.servers.length === 1 ? '' : 's'} answered`
                : `: not sent (${outcome.message})`
              : null}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Who offers to configure hosts on each segment this gateway listens to: IPv6
 * router advertisers and DHCP servers (gateway#72). More than one of either on
 * a segment raises an incident; an operator can mark a legitimate set.
 */
export default function NetworkServicesSection({ gatewayId }: { gatewayId: string }) {
  const { data, isLoading, isError } = useGatewayConfigSources(gatewayId);
  const canUpdate = usePermission('gateway:update');

  let body;
  if (isLoading) {
    body = <Skeleton className="h-24 w-full" />;
  } else if (isError) {
    body = <p className="text-sm text-muted-foreground">Couldn't load what this gateway hears.</p>;
  } else if (!data || data.length === 0) {
    body = <p className="text-sm text-muted-foreground">This gateway has not reported its segments yet.</p>;
  } else {
    body = (
      <div className="space-y-6">
        {data.map((segment) => (
          <Segment
            key={`${segment.interface}:${String(segment.vlan)}`}
            gatewayId={gatewayId}
            segment={segment}
            canUpdate={canUpdate}
          />
        ))}
      </div>
    );
  }

  return (
    <section className="bg-card border border-border rounded-lg p-5 mt-4">
      <h2 className="text-sm font-semibold mb-1">Network services</h2>
      <p className="text-xs text-muted-foreground mb-4">
        IPv6 routers and DHCP servers this gateway hears on each segment. More than one of either raises an incident.
      </p>
      {body}
    </section>
  );
}
